/**
 * sfx.ts — procedural audio (SFX + ambient music) via WebAudio. No assets, no
 * network. Safely no-ops when AudioContext is unavailable (tests / jsdom) — it
 * never creates a scheduler interval without a real context, so tests can't leak.
 *
 * Signal chain: oscillators → sfxBus / musicBus → master → destination.
 */
type SfxName = 'click' | 'buy' | 'sell' | 'unlock' | 'reward' | 'error' | 'prestige';

const SCALE = [220, 246.94, 277.18, 329.63, 369.99, 440, 493.88, 554.37]; // A minor pentatonic-ish

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  muted = false;

  private masterVolume = 0.8;
  private musicVolume = 0.5;
  private sfxVolume = 0.8;
  private musicEnabled = true;

  private musicTimer: ReturnType<typeof setInterval> | null = null;
  private nextNoteTime = 0;
  private step = 0;

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      const ctx: AudioContext = new AC();
      const master = ctx.createGain();
      const musicBus = ctx.createGain();
      const sfxBus = ctx.createGain();
      musicBus.connect(master);
      sfxBus.connect(master);
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      this.musicBus = musicBus;
      this.sfxBus = sfxBus;
      this.applyGains();
    } catch {
      this.ctx = null;
    }
    return this.ctx;
  }

  private applyGains(): void {
    if (!this.ctx || !this.master || !this.musicBus || !this.sfxBus) return;
    const m = this.muted ? 0 : this.masterVolume;
    this.master.gain.setValueAtTime(m, this.ctx.currentTime);
    this.musicBus.gain.setValueAtTime(this.musicEnabled ? this.musicVolume : 0, this.ctx.currentTime);
    this.sfxBus.gain.setValueAtTime(this.sfxVolume, this.ctx.currentTime);
  }

  /** Push settings volumes (0..1) into the live mix. */
  setVolumes(opts: { master?: number; music?: number; sfx?: number; musicEnabled?: boolean; muted?: boolean }): void {
    if (opts.master !== undefined) this.masterVolume = opts.master;
    if (opts.music !== undefined) this.musicVolume = opts.music;
    if (opts.sfx !== undefined) this.sfxVolume = opts.sfx;
    if (opts.musicEnabled !== undefined) this.musicEnabled = opts.musicEnabled;
    if (opts.muted !== undefined) this.muted = opts.muted;
    this.applyGains();
    if (this.musicEnabled && !this.muted) this.startMusic();
    else this.stopMusic();
  }

  /** Resume the context after a user gesture, then start music if enabled. */
  unlock(): void {
    const c = this.ensure();
    if (!c) return;
    if (c.state === 'suspended') void c.resume();
    if (this.musicEnabled && !this.muted) this.startMusic();
  }

  // ── Music: a cheap, loopable ambient bed via a lookahead scheduler ──
  startMusic(): void {
    const c = this.ensure();
    if (!c || this.musicTimer !== null) return;
    this.nextNoteTime = c.currentTime + 0.1;
    this.musicTimer = setInterval(() => this.scheduleMusic(), 100);
  }

  stopMusic(): void {
    if (this.musicTimer !== null) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
  }

  private scheduleMusic(): void {
    const c = this.ctx;
    const bus = this.musicBus;
    if (!c || !bus) return;
    // Schedule notes that fall within the next ~250ms.
    while (this.nextNoteTime < c.currentTime + 0.25) {
      const t = this.nextNoteTime;
      const root = SCALE[this.step % SCALE.length]!;
      // Soft pad chord (root + fifth) and an occasional sparkle.
      this.note(c, bus, t, root / 2, 1.6, 'sine', 0.16);
      this.note(c, bus, t, root, 1.6, 'triangle', 0.08);
      if (this.step % 4 === 0) this.note(c, bus, t, root * 2, 0.9, 'sine', 0.05);
      this.step++;
      this.nextNoteTime += 0.9; // ~67 BPM bed
    }
  }

  private note(c: AudioContext, dest: AudioNode, at: number, freq: number, dur: number, type: OscillatorType, gain: number): void {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, at);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(gain, at + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g).connect(dest);
    osc.start(at);
    osc.stop(at + dur + 0.05);
  }

  // ── SFX ──
  play(name: SfxName): void {
    const c = this.ensure();
    if (!c || this.muted) return;
    const dest = this.sfxBus ?? c.destination;
    const now = c.currentTime;
    switch (name) {
      case 'click': this.tone(c, dest, now, 220 + Math.random() * 40, 0.05, 'square', 0.06); break;
      case 'buy': this.tone(c, dest, now, 440, 0.08, 'triangle', 0.08); this.tone(c, dest, now + 0.06, 660, 0.08, 'triangle', 0.07); break;
      case 'sell': this.tone(c, dest, now, 520, 0.06, 'sine', 0.08); this.tone(c, dest, now + 0.05, 780, 0.07, 'sine', 0.07); break;
      case 'unlock': this.tone(c, dest, now, 392, 0.1, 'sawtooth', 0.07); this.tone(c, dest, now + 0.09, 587, 0.12, 'sawtooth', 0.07); break;
      case 'reward': [523, 659, 784].forEach((f, i) => this.tone(c, dest, now + i * 0.07, f, 0.12, 'triangle', 0.08)); break;
      case 'prestige': [392, 523, 659, 784, 1046].forEach((f, i) => this.tone(c, dest, now + i * 0.08, f, 0.16, 'sine', 0.09)); break;
      case 'error': this.tone(c, dest, now, 160, 0.12, 'sawtooth', 0.06); break;
    }
  }

  private tone(c: AudioContext, dest: AudioNode, at: number, freq: number, dur: number, type: OscillatorType, gain: number): void {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, at);
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(gain, at + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g).connect(dest);
    osc.start(at);
    osc.stop(at + dur + 0.02);
  }
}
